import React from 'react';
import { Link } from 'react-router-dom';
import { Page } from '../../components';
import DhobaraPageLayout from '../../components/DhobaraPageLayout/DhobaraPageLayout';
import css from './WhyDhobaraPage.module.css';

// Use the same images already imported in the homepage
import formalImg from '../../assets/dhobara-formal-attire.jpg';
import embroideryImg from '../../assets/dhobara-sellers-embroidery.jpg';

const WHY_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://dhobara.com/#organization',
      name: 'Dhobara',
      url: 'https://dhobara.com',
      description:
        'Dhobara is the marketplace for pre-owned South Asian luxury designer fashion — authenticated, professionally managed, and built on a generations-old tradition of sharing and circularity.',
      foundingDate: '2024',
      sameAs: ['https://www.instagram.com/dhobaraofficial'],
    },
    {
      '@type': 'AboutPage',
      '@id': 'https://dhobara.com/why-dhobara',
      name: 'Why Dhobara | Pre-Owned South Asian Luxury, Reimagined',
      url: 'https://dhobara.com/why-dhobara',
      description:
        'Learn why Dhobara exists — a marketplace built to give beautiful South Asian designer wear a second life, rooted in a tradition of sharing and circularity.',
      publisher: { '@id': 'https://dhobara.com/#organization' },
      mainEntity: { '@id': 'https://dhobara.com/#organization' },
    },
  ],
};

const WhyDhobaraPage = () => (
  <Page
    title="Why Dhobara | Pre-Owned South Asian Luxury, Reimagined"
    description="Learn why Dhobara exists — a marketplace built to give beautiful South Asian designer wear a second life, rooted in a tradition of sharing and circularity."
    scrollingDisabled={false}
  >
  <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(WHY_SCHEMA) }} />
  <DhobaraPageLayout>
    {/* Hero */}
    <div className={css.heroSection}>
      <span className={css.eyebrow}>Our Story</span>
      <h1 className={css.heroTitle}>Dhobara pahan lo!</h1>
      <p className={css.heroSubtitle}>
        Luxury deserves a second life.<br />
        South Asian designer fashion, rediscovered.
      </p>
    </div>

    {/* Who we are — image left, text right */}
    <div className={css.splitSection}>
      <div className={css.splitGrid}>
        <div className={css.imageWrap}>
          <img src={formalImg} alt="Formal attire detail" />
        </div>
        <div className={css.textBlock}>
          <span className={css.textEyebrow}>Who We Are</span>
          <h2 className={css.textTitle}>
            A couple of desis who grew tired of<br />
            <em>closets full of waiting.</em>
          </h2>
          <div className={css.textBody}>
            <p>
              We grew up watching the same thing: beautiful South Asian designer wear being stored, not worn. Wrapped in dust covers, packed into trunks, waiting for an occasion that never quite arrives.
            </p>
            <p>
              South Asian fashion is not fast. It is considered, expensive, and meaningful. Which is exactly why it deserves better than a wardrobe shelf.
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Pull quote */}
    <div className={css.quoteBand}>
      <div className={css.quoteInner}>
        <p className={css.quoteText}>
          "Lending a lehenga to a cousin, gifting a shalwar kameez to a friend, passing a treasured saree down through generations —
          <span className={css.quoteAccent}> this is how our wardrobes have always worked.</span> The original circular economy, practised long before the term existed."
        </p>
      </div>
    </div>

    {/* Building on heritage — text left, image right */}
    <div className={css.splitSection}>
      <div className={css.splitGridReverse}>
        <div className={css.textBlock}>
          <span className={css.textEyebrow}>Building on Heritage</span>
          <h2 className={css.textTitle}>
            Clothes were<br /><em>never just clothes.</em>
          </h2>
          <div className={css.textBody}>
            <p>
              Borrowing your cousin's bridal lehenga. Passing your mother's embroidered shawl to a younger sister. Gifting a barely-worn shalwar kameez to someone who'd treasure it more.
            </p>
            <p>
              They carried memories, milestones, and love. Dhobara is that same spirit, reimagined for today — a marketplace where your cherished pieces find their next moment, and someone else writes their own story in them.
            </p>
          </div>
        </div>
        <div className={css.imageWrap}>
          <img src={embroideryImg} alt="Embroidery and bridal detail" />
        </div>
      </div>
    </div>

    {/* Three principles */}
    <div className={css.principlesSection}>
      <div className={css.principlesGrid}>
        <div>
          <h3 className={css.principleTitle}>Sustainability without sacrifice</h3>
          <p className={css.principleBody}>The most responsible thing you can do with a beautiful garment is let it be beautiful again.</p>
        </div>
        <div>
          <h3 className={css.principleTitle}>Professionally managed</h3>
          <p className={css.principleBody}>A trusted marketplace where sellers aren't burdened with logistics and buyers access pieces at prices that reflect reality.</p>
        </div>
        <div>
          <h3 className={css.principleTitle}>Built on community</h3>
          <p className={css.principleBody}>Continuing a tradition of sharing that has lived in South Asian wardrobes for generations.</p>
        </div>
      </div>
    </div>

    {/* CTA */}
    <div className={css.ctaSection}>
      <p className={css.ctaLine1}>On someone else's mehndi.</p>
      <p className={css.ctaLine2}>At someone else's Eid.</p>
      <p className={css.ctaLine3}>Dhobara pahan lo!</p>
      <p className={css.ctaBody}>Ready to give your designer pieces a second life?</p>
      <Link to="/dashboard" className={css.btnDark}>Sell With Us</Link>
    </div>
  </DhobaraPageLayout>
  </Page>
);

export default WhyDhobaraPage;
